import { isString } from 'lodash';
import type { AvailabilityEnum } from 'Components/widgets/product/Product.mapper';
import 'Components/widgets/product/Product.mapper';
type PriceArgs = {
  date?: string;
  duration?: number;
  departureCity?: string;
  forceWithoutTransport?: boolean | null | undefined;
  optimizedPriceCalls?: boolean | null | undefined;
  dateStart?: string;
  dateEnd?: string;
};
export type Args = PriceArgs & {
  content?: string;
  isMice: boolean;
  release?: string;
  offerId?: string;
  offerName?: string;
  tourDurationPlaceholder?: string | null | undefined;
  cruiseTimingPlaceholder?: string | null | undefined;
  cruiseDurationPlaceholder?: string | null | undefined;
  dateFormat?: string | null | undefined;
};
export type Options = {
  withoutPrice: boolean;
};
export type ThumbnailChildcareAvailabilities = Array<{
  id: string;
  availability: AvailabilityEnum;
  ageMin: number;
  ageMax: number;
  isIncluded?: boolean;
  remainingStock: number | null;
  timeSlot: 'MORNING' | 'AFTERNOON' | '';
}>;
type ChildcareDetail = {
  id: string;
  included: boolean;
  ageInMonths: {
    min: number;
    max: number;
  };
};
export type ThumbnailProduct = {
  readonly accommodationsIntroduction?: {
    roomsCount: string;
  };
  readonly badgeIcon: string;
  readonly childcareAvailabilities?: ThumbnailChildcareAvailabilities;
  readonly childcareDetails?: Array<ChildcareDetail>;
  readonly color: string;
  readonly comfort: number;
  readonly comfortLabel: string;
  readonly coordinates?: [number, number];
  readonly departureDateOn?: string;
  readonly destination: ProductDestination;
  readonly duration: string;
  readonly hasECSpace?: boolean;
  readonly href: string;
  readonly id: string;
  readonly image?: string;
  readonly images: Array<string>;
  readonly isCruise?: boolean;
  readonly isDoubleSeason: boolean;
  readonly isExclusiveCollection?: boolean;
  readonly isExplicitEC?: boolean;
  readonly isJoyview: boolean;
  readonly isResort?: boolean;
  readonly isSummer?: boolean;
  readonly isTour?: boolean;
  readonly isVilla?: boolean;
  readonly isWinter?: boolean;
  readonly mice?: {
    bestPrice: FormattedPrice;
    currency: string;
    unit: string;
  };
  readonly minAuthorizedAge?: {
    value: number;
  };
  readonly name: string;
  readonly openingStatus: string;
  readonly price?: ProductPrice;
  readonly productMeetingRooms?: {
    meetingRooms: ReadonlyArray<{
      shapes: ReadonlyArray<{
        maxCapacity: number;
      }>;
    }>;
  };
  readonly rangeId: string;
  readonly rangeLabel: string;
  readonly resortId: string;
  readonly seasonName?: string;
  readonly seasonTrad: string;
  readonly tags?: Array<{
    backgroundColor: string;
    textColor: string;
    title: string;
  }>;
  readonly thematics: Array<{
    readonly id: string;
    readonly label: string;
  }>;
  readonly timingDescription: string;
  readonly title: string;
  readonly transportType: string;
  readonly tripadvisor?: {
    rating: {
      basedOn: string;
      title: string;
    };
    review: {
      tripadvisorTraveler: string;
      reviewBy: string;
      reviewDate: string;
    };
    menubar: {
      awards: string;
      ratings: string;
      reviews: string;
    };
  };
  readonly type: ProductType;
};
type CleanedArgs = {
  content: string;
  release: string;
  isMice: boolean;
  cruiseTimingPlaceholder: string | null | undefined;
  cruiseDurationPlaceholder: string | null | undefined;
  date: string;
  dateFormat: string | null | undefined;
  departureCity: string;
  duration: number | string | null;
  forceWithoutTransport: boolean;
  offerId: string;
  offerName: string;
  optimizedPriceCalls: boolean;
  tourDurationPlaceholder: string | null | undefined;
  dateStart: string;
  dateEnd: string;
};

const generateCmsArgs = (content, release) => `content : "${content}" release : "${release}"`;

const getPriceArgs = (args: CleanedArgs): Array<Array<any>> => {
  return [
    'offerId',
    'offerName',
    'date',
    'dateStart',
    'dateEnd',
    'duration',
    'departureCity',
    'forceWithoutTransport',
    'optimizedPriceCalls',
  ]
    .filter((key) => args[key])
    .map((key) => {
      const value = isString(args[key]) ? `"${args[key]}"` : args[key];
      return [key, value];
    });
};

const buildPriceGraphqlArgs = (args: Array<Array<string>>): string =>
  args.length ? `(${args.map((priceArg) => priceArg.join(' : ')).join(' ')})` : '';

export function getPriceFields(args: CleanedArgs): string {
  const { content, release, isMice } = args;
  const cmsArgs = generateCmsArgs(content, release);
  const priceArgs = buildPriceGraphqlArgs(getPriceArgs(args));

  if (isMice) {
    return `
    mice {
      bestPrice {
        value
        formattedValue
      }
      unit(${cmsArgs})
    }
    `;
  }

  return `
    price ${priceArgs} {
      bestPrice {
        value
        formattedValue
      }
      initialPrice {
        value
        formattedValue
      }
      unit(${cmsArgs})
      currency
      termsAndConditions {
        accommodationComfortLabel
        departureCity {
          name
          value
        }
        departureDate
        duration
        durationFormatted(${cmsArgs})
        flightDuration
        numberOfAdults
        offerId
      }
    }`;
}

function getThumbnailBaseFields(args: CleanedArgs): string {
  const {
    isMice,
    offerName,
    tourDurationPlaceholder,
    cruiseTimingPlaceholder,
    cruiseDurationPlaceholder,
    dateFormat,
    content,
    release,
  } = args;
  // use typeof string since we cannot be sure that there are no empty strings on the CMS
  const durationArgs =
    typeof tourDurationPlaceholder === 'string' && typeof cruiseDurationPlaceholder === 'string'
      ? `(message : "${tourDurationPlaceholder}", cruiseMessage : "${cruiseDurationPlaceholder}")`
      : '';
  const timingDescriptionArgs =
    typeof cruiseTimingPlaceholder === 'string' && typeof dateFormat === 'string'
      ? `(message : "${cruiseTimingPlaceholder}" dateFormat : "${dateFormat}")`
      : '';
  return `
    accommodationsIntroduction {
      roomsCount
    }
    badgeIcon
    childcareDetails {
      ageInMonths {
        min
        max
      }
      id
      included
    }
    color
    comfort
    comfortLabel
    coordinates {
      latitude
      longitude
    }
    destination {
      area
      country
    }
    duration${durationArgs}
    hasECSpace
    href(offerName : "${offerName}")
    id
    images
    isCruise
    isDoubleSeason
    isExclusiveCollection
    isExplicitEC
    isUrbanOasis
    isJoyview
    isResort
    isSummer
    isTour
    isVilla
    isWinter
    minAuthorizedAge {
      value
    }
    ${
      isMice
        ? `productMeetingRooms {
      meetingRooms {
        shapes {
          maxCapacity
        }
      }
    }`
        : ''
    }
    name
    openingStatus
    rangeId
    rangeLabel
    resortId
    seasonName
    seasonTrad(${generateCmsArgs(content, release)})
    tags {
      backgroundColor
      textColor
      title
    }
    thematics {
      label
    }
    timingDescription${timingDescriptionArgs}
    title
    transportType
    type`;
}

export function cleanupArgs(args: Args): CleanedArgs {
  return {
    content: args.content || '',
    isMice: args.isMice,
    release: args.release || '',
    cruiseTimingPlaceholder: args.cruiseTimingPlaceholder,
    cruiseDurationPlaceholder: args.cruiseDurationPlaceholder,
    date: args.date || '',
    dateFormat: args.dateFormat,
    departureCity: args.departureCity || '',
    duration: args.duration || null,
    dateStart: args.dateStart || '',
    dateEnd: args.dateEnd || '',
    forceWithoutTransport: !!args.forceWithoutTransport,
    offerId: args.offerId || '',
    offerName: args.offerName || '',
    optimizedPriceCalls: args.optimizedPriceCalls || false,
    tourDurationPlaceholder: args.tourDurationPlaceholder,
  };
}

function makeResortThumbnailQuery(args: Args, options?: Options): string {
  const { withoutPrice = false } = options || {};
  const priceFields = !withoutPrice ? getPriceFields(cleanupArgs(args)) : '';
  return `${getThumbnailBaseFields(cleanupArgs(args))}${priceFields}`;
}

export default makeResortThumbnailQuery;
